import { N8nTrackLeaderboardEntry } from '@shared/models/n8n-track-leaderboard.model';
import { LeaderboardEntry } from '../components/sd-leaderboard-list/sd-leaderboard-list.component';
import { LeaderboardOverviewStats, LeaderboardPodiumDriver } from '../components/sd-leaderboard-podium/sd-leaderboard-podium.component';

export function formatCarName(brand: string, title: string): string {
    if (title.toLowerCase().startsWith(brand.toLowerCase())) return title;
    return `${brand} ${title}`;
}

export function formatGap(ms: number): string {
    return `+${(ms / 1000).toFixed(3)}`;
}

/** Valide Einträge aufsteigend nach Bestzeit, invalide (ohne valide Runde) ans Ende. */
export function sortLeaderboard(data: N8nTrackLeaderboardEntry[]): N8nTrackLeaderboardEntry[] {
    const valid = data.filter((e) => e.validLaps > 0).sort((a, b) => a.bestLaptimeMsValid - b.bestLaptimeMsValid);
    const invalid = data
        .filter((e) => e.validLaps === 0)
        .sort((a, b) => {
            const aFastest = a.laps.length ? Math.min(...a.laps.map((l) => l.laptimeMs)) : Infinity;
            const bFastest = b.laps.length ? Math.min(...b.laps.map((l) => l.laptimeMs)) : Infinity;
            return aFastest - bFastest;
        });
    return [...valid, ...invalid];
}

/** Erwartet vorsortierte Eingabe (siehe sortLeaderboard). */
export function toLeaderboardEntries(entries: N8nTrackLeaderboardEntry[], winnerCount?: number | null): LeaderboardEntry[] {
    if (entries.length === 0) return [];
    const fastestMs = entries.find((e) => e.validLaps > 0)?.bestLaptimeMsValid ?? 0;
    const validEntries = entries.filter((e) => e.validLaps > 0);
    const validCount = validEntries.length;
    const cutActive = typeof winnerCount === 'number' && Number.isInteger(winnerCount) && winnerCount > 0 && validCount > winnerCount;
    const cutoffMs = cutActive ? validEntries[winnerCount! - 1].bestLaptimeMsValid : 0;
    let validRank = 0;
    return entries.map((entry) => {
        const hasValidTime = entry.validLaps > 0;
        if (hasValidTime) validRank++;
        const position = hasValidTime ? validRank : null;
        const isAtRisk = cutActive && position === winnerCount;
        const isBelowCut = cutActive && position !== null && position > winnerCount!;
        let abstand = '';
        if (hasValidTime && validRank > 1) {
            abstand = isBelowCut ? formatGap(entry.bestLaptimeMsValid - cutoffMs) : formatGap(entry.bestLaptimeMsValid - fastestMs);
        }
        return {
            position,
            name: entry.playerName,
            car: formatCarName(entry.bestCarBrand, entry.bestCarTitle),
            time: hasValidTime ? entry.bestLaptimeFormattedValid : '–',
            abstand,
            isAtRisk,
            isBelowCut,
            validLaps: entry.validLaps,
            invalidLaps: entry.invalidLaps,
            laps: [...entry.laps].sort((a, b) => a.laptimeMs - b.laptimeMs),
        };
    });
}

export function toPodium(
    entries: N8nTrackLeaderboardEntry[],
): [LeaderboardPodiumDriver, LeaderboardPodiumDriver, LeaderboardPodiumDriver] | null {
    const valid = entries.filter((e) => e.validLaps > 0);
    if (valid.length < 3) return null;
    return [0, 1, 2].map((i) => ({
        name: valid[i].playerName,
        time: valid[i].bestLaptimeFormattedValid,
        car: formatCarName(valid[i].bestCarBrand, valid[i].bestCarTitle),
    })) as [LeaderboardPodiumDriver, LeaderboardPodiumDriver, LeaderboardPodiumDriver];
}

export function toStats(entries: N8nTrackLeaderboardEntry[]): LeaderboardOverviewStats | null {
    const valid = entries.filter((e) => e.validLaps > 0);
    if (valid.length === 0) return null;
    return {
        drivers: entries.length,
        fastestTimeOverall: valid[0].bestLaptimeFormattedValid,
        totalLapsDriven: entries.reduce((sum, e) => sum + e.totalLaps, 0),
    };
}
